import { Alert, Gray100, typeCompact14RegularProperties } from '@traceair/tds';
import { classes, stylesheet } from 'typestyle';
import { GeorefData, UploadData, useUploadStore } from '../../../../../store/uploadStore';
import { useHandlePpkGeorefAdd } from './useHandlePpkGeorefAdd/useHandlePpkGeorefAdd';
import { ProjectData } from '../../types';
import { ScheduledFlight } from '../../../../../types';
import { GeorefUploadProgress } from './uploadProgress/georefUploadProgress/GeorefUploadProgress';
import { FileUpload } from './FileUpload';

const GEOREF_ACCEPT = { 'text/plain': ['.txt'] };

const css = stylesheet({
  heading: {
    ...typeCompact14RegularProperties,
    marginBottom: 8,
    color: Gray100,
  },
  restorationNotice: {
    marginBottom: 8,
  },
  georefUpload: {
    marginBottom: 0,
  },
  georefUploadPicked: {
    $nest: {
      '&&': {
        marginBottom: 24,
      },
    },
  },
  georefError: {
    marginBottom: 6,
  },
});

interface Props extends ProjectData {
  schedule?: ScheduledFlight;
  hasGeorefError: boolean;
  upload: UploadData | undefined;
}

export const FlightUploadGeoref = ({ customer, site, schedule, hasGeorefError, upload }: Props) => {
  const { getGeoref, setGeoref } = useUploadStore();

  const wrappedGetGeoRef = () => getGeoref(customer.id, site.id, schedule?.airtableRecordId);
  const wrappedSetGeoRef = (geoRef: GeorefData) => setGeoref(customer.id, site.id, schedule?.airtableRecordId, geoRef);

  const handlePpkGeoRefUpload = useHandlePpkGeorefAdd(customer, site, wrappedGetGeoRef, wrappedSetGeoRef);

  const georef = wrappedGetGeoRef();

  return (
    <div className={classes(css.georefUpload, georef && !georef.meta.error && css.georefUploadPicked)}>
      <div className={css.heading}>Georeferencing file with the photo coordinates</div>

      {upload && !georef && (
        <Alert
          className={css.restorationNotice}
          type="attention"
          title="Upload the georeferencing file"
          description="To resume the incomplete upload, please select the same georeferencing file."
        />
      )}

      {!georef && (
        <>
          <FileUpload
            accept={GEOREF_ACCEPT}
            description="Accepted format: TXT"
            disabled={!site.hasUploadPermission}
            dragOverText="Drop a file here"
            fileFormatIcons={['TXT']}
            onChange={handlePpkGeoRefUpload}
            title="Drop Georeferencing file here, or"
          />
        </>
      )}

      {georef && <GeorefUploadProgress customer={customer} site={site} schedule={schedule} georef={georef} />}

      {hasGeorefError && (
        <Alert
          className={css.georefError}
          type="warning"
          title="Failed to parse the georeferencing file"
          description="Please ensure that you've uploaded the correct file and it is not empty"
        />
      )}
    </div>
  );
};
